/**
 * PredictiveBuyCard - Card para envio de ordens de preditivos
 *
 * Uso:
 * - variant="card" (default): com borda, para páginas
 * - variant="embedded": sem borda, para dialogs/modais
 */
import { useState, useEffect, useRef } from "react";
import { AxiosError } from "axios";
import {
  Flex,
  Text,
  useMantineTheme,
  Button,
  Tooltip,
  Collapse,
  NumberInput,
  Center,
} from "@mantine/core";
import { InfoCircle, Lock } from "iconsax-react";
import { useTranslation } from "react-i18next";
import {
  ETypePredictive,
  ETypeInstrument,
  IPredictiveProduct,
  IPredictiveProductInstrument,
  IPostPredictiveOrder,
  postPredictiveOrder,
  EInstrumentType,
} from "../../hooks/usePredictives";
import { meetingDate } from "../../utils/date";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import Modal from "../Modals/ModalResponse";
import { useNavigate } from "react-router-dom";
import TextTooltipValue from "../../pages/Marketplace/Predictives/ModalBuy/TextTooltipValue";
import { useAuth } from "../../context/auth";
import { EValidityType } from "../../utils/predictiveValidity";
import { formatCurrency } from "../../utils/currency";
import SimulationModal from "./SimulationModal";
import InputNumber from "../Form/InputNumber";
import DataInputs, { IExpireDateAndValidity } from "./DataInputs";
import NotNeedConfirm from "./NotNeedConfirm";
import ConfirmModal from "./ConfirmModal";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import moment from "moment";

// Schema condicional: MARKET_PROTECTION só quantity, LIMIT quantity + price
const predictiveOrderSchema = yup.object().shape({
  predictive_instrument_id: yup.number().required(),
  type: yup.string().oneOf(["MARKET_PROTECTION", "LIMIT"]).required(),
  side: yup.string().oneOf(["BUY", "SELL"]).required(),
  quantity: yup
    .number()
    .min(1, "Quantidade é obrigatória")
    .required("Quantidade é obrigatória"),
  price: yup.number().when("type", {
    is: "LIMIT",
    then: (schema) =>
      schema.min(1, "Preço é obrigatório").required("Preço é obrigatório"),
    otherwise: (schema) => schema.optional().nullable(),
  }),
  validity: yup.string().optional(),
  expire_date: yup.string().optional().nullable(),
});

export type PredictiveBuyCardVariant = "card" | "embedded";

export interface PredictiveBuyCardProps {
  data: IPredictiveProduct | undefined;
  selectedInstrument: IPredictiveProductInstrument | null;
  selectedSide: ETypeInstrument;
  onSideChange: (side: ETypeInstrument) => void;
  /** "card" (com borda) ou "embedded" (para dialogs) - default: "card" */
  variant?: PredictiveBuyCardVariant;
  /** Callback após envio da ordem com sucesso */
  onOrderSuccess?: () => void;
  /** Callback para fechar dialog/modal */
  onClose?: () => void;
}

export default function PredictiveBuyCard({
  data,
  selectedInstrument,
  selectedSide,
  onSideChange,
  variant = "card",
  onOrderSuccess,
  onClose,
}: PredictiveBuyCardProps) {
  const theme = useMantineTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isNotNeedConfirm = localStorage.getItem("notNeedConfirm");
  const valueBoolean = isNotNeedConfirm === "true";
  const valueString = valueBoolean ? "false" : "true";
  const [notNeedConfirm, setNotNeedConfirm] = useState(valueBoolean || false);
  const { isAuthenticated, accessExternalLink } = useAuth();
  const [isSuccessModal, setIsSuccessModal] = useState(false);
  const [isLoginModal, setIsLoginModal] = useState(false);
  const [isErrorModal, setIsErrorModal] = useState(false);
  const [isSimulateModal, setIsSimulateModal] = useState(false);
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);

  const defaultFormValues = {
    type: ETypePredictive.MARKET_PROTECTION,
    side: ETypeInstrument.BUY,
    quantity: 0,
    predictive_instrument_id: selectedInstrument?.id,
    validity: EValidityType.TODAY,
    expire_date: undefined,
    price: undefined,
  };

  const { setValue, watch, handleSubmit, reset } =
    useForm<IPostPredictiveOrder>({
      resolver: yupResolver(predictiveOrderSchema),
      defaultValues: defaultFormValues,
    });

  const numberOfContracts = Number(watch("quantity")) || 0;
  const priceLimit = Number(watch("price")) || 0;
  const isSideInstrumentIsSell = watch("side") === ETypeInstrument.SELL;
  const isSideInstrumentIsBuy = watch("side") === ETypeInstrument.BUY;
  const isLimit = watch("type") === ETypePredictive.LIMIT;
  const isMarketProtection =
    watch("type") === ETypePredictive.MARKET_PROTECTION;
  const expireDateAndValidity: IExpireDateAndValidity = {
    expire_date: watch("expire_date") || "",
    validity: (watch("validity") as EValidityType) || EValidityType.TODAY,
  };
  const dataLimit = !!data?.expiration
    ? moment.utc(new Date(data?.expiration)).add(1, "day").toDate()
    : new Date();

  const resetFormFields = () => {
    reset({
      ...defaultFormValues,
      side: selectedSide,
      predictive_instrument_id: selectedInstrument?.id,
    });
  };

  // Sincroniza o side do form com o selectedSide da prop
  useEffect(() => {
    setValue("side", selectedSide);
  }, [selectedSide, setValue]);

  // Reseta os campos do formulário quando o instrumento selecionado muda
  useEffect(() => {
    if (selectedInstrument?.id) {
      resetFormFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstrument?.id]);

  // Cálculos do resumo
  // Valor bruto: NÃO usa bid_price, SIM usa ask_price
  const selectedSidePrice = isSideInstrumentIsSell
    ? selectedInstrument?.level1.bid_price || 0
    : selectedInstrument?.level1.ask_price || 0;

  // Valor bruto = preço * quantidade
  const grossValue = selectedSidePrice * numberOfContracts;

  // Prêmio total = (bid + calculated_ask) * quantidade
  const premium = selectedInstrument?.premium || 0;
  const rewardTotal = premium * numberOfContracts;

  // Prêmio bruto possível = prêmio total - valor bruto
  const grossPossibleReward = rewardTotal - grossValue;

  // Emolumentos: mínimo entre 25% do prêmio bruto possível e 1.10% do valor bruto
  const limit25Percent = grossPossibleReward * 0.25;
  const emoluments = grossValue * 0.011;
  const fees = Math.min(limit25Percent, emoluments);

  // Valor total da transação = valor bruto + emolumentos
  const totalTransaction = grossValue + fees;

  // Taxa de corretagem: 3,5% sobre (prêmio total - valor da transação)
  const brokerage = (rewardTotal - totalTransaction) * 0.035;

  // Lucro possível depende do lado:
  // SIM: prêmio total - valor transação - corretagem
  // NÃO: valor bruto - emolumentos - corretagem
  const profitPossible = isSideInstrumentIsSell
    ? grossValue - fees - brokerage
    : rewardTotal - totalTransaction - brokerage;

  // Valor total da transação exibido:
  // SIM: valor bruto + emolumentos
  // NÃO: valor pagto antecipado + emolumentos (grossPossibleReward + fees)
  const valueTransaction = isSideInstrumentIsSell
    ? grossPossibleReward + fees
    : grossValue + fees;

  const valueSelectedIsZero = isSideInstrumentIsSell
    ? selectedInstrument?.calculated_ask === 0
    : selectedInstrument?.level1?.ask_price === 0;
  // const valueSelectedIsZero = false;

  const { mutate, isPending } = useMutation({
    mutationFn: postPredictiveOrder,
    onSuccess: () => {
      setIsOpenConfirmModal(false);
      resetFormFields();
      setIsSuccessModal(true);
      onOrderSuccess?.();
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === 401) {
        setIsLoginModal(true);
      } else {
        setIsErrorModal(true);
      }
    },
  });

  const onSubmit = async (formData: IPostPredictiveOrder) => {
    if (!selectedInstrument) return;
    mutate({
      ...formData,
      predictive_instrument_id: selectedInstrument.id,
    });
  };

  const handleRedirect = () => {
    navigate("/operacoes/predix");
  };

  const handleRedirectLogin = () => {
    if (accessExternalLink) {
      window.open(accessExternalLink, "_target");
    } else {
      navigate("/autenticação");
    }
  };

  const handleCloseSuccessModal = () => {
    setIsSuccessModal(false);
    onClose?.();
  };

  const handleCloseLoginModal = () => {
    setIsLoginModal(false);
  };

  const handleCloseErrorModal = () => {
    setIsErrorModal(false);
  };

  const handleCloseSimulateModal = () => {
    setIsSimulateModal(false);
  };

  const handleOpenConfirmModal = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsOpenConfirmModal(true);
  };

  const handleCloseConfirmModal = () => {
    setIsOpenConfirmModal(false);
  };

  const handleNotNeedConfirm = () => {
    localStorage.setItem("notNeedConfirm", valueString);
    setNotNeedConfirm(!valueBoolean);
  };

  const handleOnChange = (expireDateAndValidity: IExpireDateAndValidity) => {
    if (expireDateAndValidity.validity === EValidityType.UNTIL_DATE) {
      setValue("expire_date", expireDateAndValidity.expire_date);
    } else {
      setValue("expire_date", undefined);
    }
    setValue("validity", expireDateAndValidity.validity);
  };

  const containerStyles =
    variant === "card"
      ? {
          borderRadius: "0.75rem",
          minWidth: "320px",
          maxWidth: "380px",
          alignSelf: "flex-start" as const,
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
        }
      : {
          width: "100%",
        };

  const content = (
    <>
      <Flex
        gap="10px"
        w="100%"
        justify="space-between"
        align="center"
        direction={"column"}
      >
        <Text
          w={"100%"}
          style={{
            color: theme.colors.complementary[3],
            background: theme.colors.complementaryOpacity[3],
            padding: "0.4rem 0.7rem",
            borderRadius: "0.5rem",
            fontSize: "14px",
            fontWeight: 600,
            textAlign: "center",
            whiteSpace: "nowrap",
            flex: "1",
          }}
        >
          {meetingDate({
            date: data?.expiration || "",
            category: data?.category || "",
            t,
          })}
        </Text>
        {selectedInstrument && (
          <Text
            w={"100%"}
            style={{
              color:
                selectedInstrument.direction === EInstrumentType.increase
                  ? theme.colors.complementary[0]
                  : selectedInstrument.direction === EInstrumentType.neutral
                  ? theme.colors.complementary[7]
                  : theme.colors.complementary[2],
              background:
                selectedInstrument.direction === EInstrumentType.increase
                  ? theme.colors.complementaryOpacity[0]
                  : selectedInstrument.direction === EInstrumentType.neutral
                  ? theme.colors.complementaryOpacity[7]
                  : theme.colors.complementaryOpacity[2],
              padding: "0.4rem 0.7rem",
              borderRadius: "0.5rem",
              fontSize: "14px",
              fontWeight: 600,
              textAlign: "center",
              flex: "1",
            }}
          >
            {selectedInstrument.label}
          </Text>
        )}
      </Flex>

      <Text
        style={{
          color: theme.colors.basic[5],
          fontSize: "1rem",
          fontWeight: 600,
          lineHeight: "140%",
        }}
      >
        {data?.title}
      </Text>

      <form
        onSubmit={
          notNeedConfirm ? handleSubmit(onSubmit) : handleOpenConfirmModal
        }
      >
        <Flex direction="column" gap="16px" w="100%">
          {/* <Flex w="100%" gap="10px">
            <Text
              onClick={() =>
                setValue("type", ETypePredictive.MARKET_PROTECTION)
              }
              style={{
                color: isMarketProtection
                  ? theme.colors.basic[0]
                  : theme.colors.basic[5],
                background: isMarketProtection
                  ? theme.colors.other[3]
                  : theme.colors.basic[0],
                padding: "0.5rem",
                borderRadius: "0.6rem",
                fontSize: "0.85rem",
                fontWeight: 600,
                textAlign: "center",
                flex: "1",
                cursor: "pointer",
                border: `1px solid`,
                borderColor: isMarketProtection
                  ? "trasparent"
                  : theme.colors.basic[2],
                transition: "all 0.2s ease-in-out",
              }}
            >
              {t("global.predictives_buy_card.market")}
            </Text>
            <Text
              onClick={() => setValue("type", ETypePredictive.LIMIT)}
              style={{
                color: isLimit ? theme.colors.basic[0] : theme.colors.basic[5],
                background: isLimit
                  ? theme.colors.other[3]
                  : theme.colors.basic[0],
                padding: "0.5rem",
                borderRadius: "0.6rem",
                fontSize: "0.85rem",
                fontWeight: 600,
                textAlign: "center",
                flex: "1",
                cursor: "pointer",
                border: `1px solid`,
                borderColor: isLimit ? "trasparent" : theme.colors.basic[2],
                transition: "all 0.2s ease-in-out",
              }}
            >
              {t("global.predictives_buy_card.limit")}
            </Text>
          </Flex> */}

          <Flex gap="12px" w="100%" justify={"space-between"} align={"center"}>
            <Button
              onClick={() => {
                setValue("side", ETypeInstrument.BUY);
                onSideChange(ETypeInstrument.BUY);
              }}
              variant="optionYes"
              data-active={isSideInstrumentIsBuy}
              styles={{
                root: {
                  width: "100%",
                  minHeight: "2.5rem !important",
                  borderRadius: "10px !important",
                },
                label: {
                  fontSize: "1rem",
                  lineHeight: "1.3rem !important",
                  height: "1.3rem !important",
                },
                inner: {
                  height: "1.3rem !important",
                },
              }}
            >
              {`${t("global.predictives.yes")}   ${
                isLimit
                  ? ""
                  : selectedInstrument?.level1_formatted?.ask_price || "R$ 0"
              }`}
            </Button>
            <Button
              onClick={() => {
                setValue("side", ETypeInstrument.SELL);
                onSideChange(ETypeInstrument.SELL);
              }}
              data-active={isSideInstrumentIsSell}
              variant="optionNo"
              styles={{
                root: {
                  width: "100%",
                  minHeight: "2.5rem !important",
                  borderRadius: "10px !important",
                },
                label: {
                  fontSize: "1rem",
                  lineHeight: "1.3rem !important",
                  height: "1.3rem !important",
                },
                inner: {
                  height: "1.3rem !important",
                },
              }}
            >
              {`${t("global.predictives.no")}   ${
                isLimit
                  ? ""
                  : selectedInstrument?.calculated_ask_formatted || "R$ 0"
              }`}
            </Button>
          </Flex>
          <Flex direction="column" gap="10px" w="100%" justify="space-between">
            <InputNumber
              label={t("global.predictives_buy_card.number_contract")}
              placeholder="0"
              min={1}
              max={10000}
              showControls={true}
              value={numberOfContracts || ""}
              onChange={(e) => {
                setValue("quantity", e || 0);
              }}
            />
            {/* <Collapse in={!!isLimit}>
              <Flex
                direction="column"
                gap="10px"
                w="100%"
                justify="space-between"
              >
                <InputNumber
                  label={t("global.predictives_buy_card.price")}
                  hasIcon
                  showControls={true}
                  placeholder={t("global.predictives_buy_card.price_range")}
                  min={1}
                  max={100}
                  value={priceLimit || ""}
                  onChange={(e) => {
                    setValue("price", e || 0);
                  }}
                />
                <DataInputs
                  handleOnChange={handleOnChange}
                  value={expireDateAndValidity}
                  dateLimit={dataLimit}
                />
              </Flex>
            </Collapse> */}
          </Flex>

          <Collapse in={!!numberOfContracts && !!selectedInstrument}>
            <Flex
              direction="column"
              gap="10px"
              w="100%"
              justify="space-between"
            >
              <TextTooltipValue
                label={t("global.predictives_buy_card.value_total.label")}
                value={valueSelectedIsZero ? 0 : valueTransaction}
                title={t("global.predictives_buy_card.value_total.title")}
              />
              {/* <TextTooltipValue
                label="Prêmio bruto sem emolumentos taxas e corretagem"
                value={rewardTotal}
                title="Prêmio Possível"
              /> */}
              <TextTooltipValue
                label={t("global.predictives_buy_card.liquid_value.label")}
                value={valueSelectedIsZero ? 0 : profitPossible}
                title={t("global.predictives_buy_card.liquid_value.title")}
              />

              <Collapse
                in={
                  !!numberOfContracts &&
                  isSideInstrumentIsSell &&
                  !!selectedInstrument &&
                  !valueSelectedIsZero
                }
              >
                <Flex
                  gap="8px"
                  align="flex-start"
                  w="100%"
                  p="8px"
                  style={{
                    background: theme.colors.alertsOpacity[0],
                    borderRadius: "8px",
                    border: "1px solid rgba(239, 68, 68, 0.2)",
                  }}
                >
                  <Center>
                    <InfoCircle
                      size={16}
                      color={theme.colors.alerts[0]}
                      variant="Bold"
                    />
                  </Center>
                  <Text size="xs" lh="140%" c="alerts.0">
                    {t("global.predictives_buy_card.info.value_block")}
                  </Text>
                </Flex>
              </Collapse>
              <Collapse in={!valueSelectedIsZero}>
                {/* Aviso informativo sobre taxas */}
                <Flex
                  gap="8px"
                  align="flex-start"
                  w="100%"
                  p="12px"
                  style={{
                    background: "rgba(0, 0, 0, 0.03)",
                    borderRadius: "8px",
                    border: "1px solid rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <Center>
                    <InfoCircle
                      size={16}
                      color={theme.colors.basic[4]}
                      variant="Bold"
                    />
                  </Center>
                  <Text size="xs" lh="140%" c="basic.4">
                    {t("global.predictives_buy_card.info.tax")}
                  </Text>
                </Flex>
              </Collapse>

              {/* <Button
                w="100%"
                variant="link"
                onClick={() => setIsSimulateModal(true)}
                h="22px"
                p="0"
                styles={{
                  root: {
                    minHeight: "22px",
                    padding: "0 24px",
                  },
                  inner: {
                    height: "22px",
                  },
                }}
              >
                Ver Simulação
              </Button> */}

              {!!isSimulateModal ? (
                <SimulationModal
                  onClose={handleCloseSimulateModal}
                  opened={isSimulateModal}
                  data={data}
                  selectedInstrument={selectedInstrument}
                  selectedSide={selectedSide}
                  numberOfContracts={numberOfContracts}
                />
              ) : null}
              <Collapse in={!valueSelectedIsZero}>
                <NotNeedConfirm
                  notNeedConfirm={notNeedConfirm}
                  handleNotNeedConfirm={handleNotNeedConfirm}
                />
              </Collapse>
              {isOpenConfirmModal ? (
                <ConfirmModal
                  data={data}
                  selectedInstrument={selectedInstrument}
                  onClose={handleCloseConfirmModal}
                  opened={isOpenConfirmModal}
                  onConfirm={handleSubmit(onSubmit)}
                  watch={watch()}
                  valueTransaction={valueTransaction}
                />
              ) : null}
            </Flex>
          </Collapse>
          {isAuthenticated ? (
            <Button
              w="100%"
              variant="primary"
              type="submit"
              loading={isPending}
              disabled={
                !selectedInstrument ||
                numberOfContracts <= 0 ||
                valueSelectedIsZero ||
                (isLimit && priceLimit === 0)
              }
              styles={{
                root: {
                  minHeight: "3rem",
                },
              }}
            >
              {!!selectedInstrument
                ? t("global.predictives_buy_card.send_order")
                : t("global.predictives_buy_card.choice_option")}
            </Button>
          ) : (
            <Button
              w="100%"
              variant="primary"
              onClick={handleRedirectLogin}
              leftIcon={<Lock size="1.2rem" color={theme.colors.basic[0]} />}
              styles={{
                root: {
                  minHeight: "3rem",
                },
              }}
            >
              {t("global.predictives_buy_card.login")}
            </Button>
          )}
        </Flex>
      </form>
    </>
  );

  return (
    <>
      <Modal
        type={"success"}
        titleSeccondButton={t("global.modal.close")}
        description={t("global.predictives_buy_card.modal_success.description")}
        contentEspecial={
          <Text
            c="basic.4"
            fz="14px"
            lh="155%"
            ta={"center"}
            w="320px"
            mb="16px"
          >
            {t("global.predictives_buy_card.modal_success.contentEspecial")}
          </Text>
        }
        open={isSuccessModal}
        setClose={handleCloseSuccessModal}
        firstClick={handleRedirect}
        titleFirstButton={t(
          "global.predictives_buy_card.modal_success.titleFirstButton"
        )}
        secondClick={handleCloseSuccessModal}
      />

      <Modal
        type={"warning"}
        titleSeccondButton={t("global.modal.close")}
        description={t("global.predictives_buy_card.modal_login.description")}
        contentEspecial={
          <Text
            c="basic.4"
            fz="14px"
            lh="155%"
            ta={"center"}
            w="320px"
            mb="16px"
          >
            {t("global.predictives_buy_card.modal_login.contentEspecial")}
          </Text>
        }
        open={isLoginModal}
        setClose={handleCloseLoginModal}
        firstClick={handleRedirectLogin}
        titleFirstButton={t(
          "global.predictives_buy_card.modal_login.titleFirstButton"
        )}
        secondClick={handleCloseLoginModal}
      />

      <Modal
        type={"danger"}
        description={t("global.predictives_buy_card.modal_error.description")}
        contentEspecial={
          <Text
            c="basic.4"
            fz="14px"
            lh="155%"
            ta={"center"}
            w="320px"
            mb="16px"
          >
            {t("global.predictives_buy_card.modal_error.contentEspecial")}
          </Text>
        }
        open={isErrorModal}
        setClose={handleCloseErrorModal}
        firstClick={handleCloseErrorModal}
        titleFirstButton={t("global.modal.close")}
      />

      <Flex
        direction="column"
        gap="20px"
        p={variant === "card" ? "1.5rem" : "0"}
        bg={theme.colors.basic[0]}
        h="fit-content"
        style={containerStyles}
      >
        {content}
      </Flex>
    </>
  );
}
